import { useEffect, useState } from 'react';

import {
  fetchPendingMemoryProposals,
  type MemoryProposal,
} from '@/lib/api/memory.js';

interface WorkspaceEntry {
  proposals: MemoryProposal[];
  listeners: Set<(rows: MemoryProposal[]) => void>;
  loading: boolean;
  /** A refresh was asked for while one was in flight, and has to run once the current one lands. */
  stale: boolean;
}

/**
 * One entry per workspace, shared by every message bubble on screen.
 *
 * Deliberately not TanStack Query. `MessageBubble` also renders in the public share viewer, which
 * bypasses every provider on purpose, so a `useQuery` here would make an optional nicety a hard
 * dependency of a surface that has to load for somebody with no session at all. A module-scope cache
 * keeps "fetch once, share everywhere" with no provider — the same shape the skill-suggestion slot uses.
 */
const cache = new Map<string, WorkspaceEntry>();

/**
 * Told whenever the shared list changes — what lets the sidebar counter, which does live in TanStack
 * Query, stay honest after somebody answers a card in the chat. Without it the badge would keep
 * counting a suggestion the person just accepted, and the app never refetches on focus.
 */
const changeListeners = new Set<() => void>();

function entryFor(workspaceId: string): WorkspaceEntry {
  const existing = cache.get(workspaceId);
  if (existing) return existing;
  const created: WorkspaceEntry = {
    proposals: [],
    listeners: new Set(),
    loading: false,
    stale: false,
  };
  cache.set(workspaceId, created);
  return created;
}

function publish(entry: WorkspaceEntry): void {
  for (const listener of entry.listeners) listener(entry.proposals);
  for (const listener of changeListeners) listener();
}

/** Subscribe to "the pending list moved". Returns the unsubscribe. */
export function onMemoryProposalsChanged(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/**
 * Load (or reload) one workspace's pending proposals and tell every mounted slot. Fails soft to the
 * list it already had: this is an unrequested nicety on top of a conversation somebody opened to do
 * something else, so an unreachable server must cost them nothing but the absence of a card.
 *
 * A request that arrives while one is in flight is remembered rather than dropped, and runs when the
 * current one lands. The extraction judge answers well after the turn, so a person sitting in the
 * conversation has a fetch in flight — issued before the proposal existed — exactly when the push
 * arrives, and dropping the second request is how the card fails to appear at all.
 */
export async function refreshMemoryProposals(
  workspaceId: string,
): Promise<void> {
  const entry = entryFor(workspaceId);
  if (entry.loading) {
    entry.stale = true;
    return;
  }
  entry.loading = true;
  try {
    entry.proposals = await fetchPendingMemoryProposals(workspaceId);
    publish(entry);
  } catch {
    /** Keep the last good list; see the note above. */
  } finally {
    entry.loading = false;
  }
  if (entry.stale) {
    entry.stale = false;
    await refreshMemoryProposals(workspaceId);
  }
}

/** Drop one proposal from the shared list, so an answered card disappears everywhere at once. */
export function forgetMemoryProposal(
  workspaceId: string,
  proposalId: string,
): void {
  const entry = cache.get(workspaceId);
  if (!entry) return;
  entry.proposals = entry.proposals.filter((row) => row.id !== proposalId);
  publish(entry);
}

/**
 * The pending proposals for a workspace. The first slot to mount triggers the single fetch; every
 * other slot reads the shared result.
 */
export function useMemoryProposals(
  workspaceId: string | null,
): MemoryProposal[] {
  const [proposals, setProposals] = useState<MemoryProposal[]>(() =>
    workspaceId ? entryFor(workspaceId).proposals : [],
  );

  useEffect(() => {
    if (workspaceId === null) {
      setProposals([]);
      return;
    }
    const entry = entryFor(workspaceId);
    setProposals(entry.proposals);
    entry.listeners.add(setProposals);
    void refreshMemoryProposals(workspaceId);
    return () => {
      entry.listeners.delete(setProposals);
    };
  }, [workspaceId]);

  return proposals;
}
